import angular from 'angular';
/**
 * The factory for `Canny`.
 */
class CannyFactory {
    constructor($window, User, cannyConfig, $log) {
        this.$window = $window;
        this.User = User;
        this.cannyConfig = cannyConfig;
        this.$log = $log;
    }
    /**
     * Validate that the canny library has been loaded.
     */
    verifyCannyExists() {
        if (this.$window.Canny === undefined) {
            console.info('Canny not loaded');
            return false;
        }
        return true;
    }
    /**
     * Load Canny SDK. Injects SDK script dynamically to the body.
     */
    async loadSDK() {
        return new Promise((resolve, reject) => {
            const existingScript = document.querySelector('#canny-jssdk');
            if (!existingScript) {
                const script = document.createElement('script');
                script.type = 'text/javascript';
                script.async = true;
                script.src = `https://canny.io/sdk.js?v=001`;
                script.id = '#canny-jssdk';
                document.body.append(script);
                script.addEventListener('load', () => {
                    resolve();
                });
                script.addEventListener('error', (e) => {
                    console.error('Error occured while loading Canny', e);
                    reject(e);
                });
            }
            else {
                resolve();
            }
        });
    }
    /**
     * Identify the current user to Canny. Will return if the User couldn't be
     * retrieved or the canny library hasn't been loaded.
     */
    identify() {
        const currentUser = this.User.getCachedCurrent();
        if (!this.verifyCannyExists() || !currentUser) {
            return;
        }
        const { id, firstName, lastName, email, userProfilePictureUrl } = currentUser;
        this.User.prototype$getCannyToken({ id })
            .$promise.then((res) => {
            const userHash = res.userHash;
            if (!userHash) {
                this.$log.warn('Canny user hash not found, skipping identify');
                return;
            }
            this.$window.Canny('identify', {
                appID: this.cannyConfig.appID,
                user: {
                    id,
                    email,
                    name: `${firstName} ${lastName}`,
                    avatarURL: 
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    userProfilePictureUrl !== undefined
                        ? `https:${userProfilePictureUrl}`
                        : null,
                },
                hash: userHash,
            });
            return undefined;
        })
            .catch((error) => {
            this.$log.error('User.getCannyToken', error);
        });
    }
    /**
     * Builds the redirect URL to jump into canny.
     *
     * @returns The redirect URL or null, if the `companyID` is missing.
     */
    getRedirectUrl() {
        const url = new URL(this.$window.location.href);
        const companyID = url.searchParams.get('companyID');
        const redirect = url.searchParams.get('redirect');
        return companyID
            ? `https://canny.io/api/redirects/sso?companyID=${companyID}&redirect=${redirect}`
            : null;
    }
    /**
     * Jump into canny. This function makes a network request to `getCannyToken`.
     *
     * @param redirectUrl - The redirect URL for Canny, created by `getRedirectUrl`.
     */
    redirectToCanny(redirectUrl) {
        const currentUser = this.User.getCachedCurrent();
        if (!currentUser) {
            return;
        }
        this.User.prototype$getCannyToken({ id: currentUser.id })
            .$promise.then((res) => {
            const ssoToken = res.token;
            this.$window.location.href = `${redirectUrl}&ssoToken=${ssoToken}`;
            return undefined;
        })
            .catch((error) => {
            this.$log.error('User.getCannyToken', error);
        });
    }
}
/* @ngInject */
const cannyFactory = ($window, User, cannyConfig, $log) => {
    return new CannyFactory($window, User, cannyConfig, $log);
};
export default angular
    .module('doxy.common.services.cannyFactory', [])
    .factory('cannyFactory', cannyFactory).name;
